import { Controller } from '@hotwired/stimulus';
import { createFocusTrap } from 'focus-trap';
import { useClickOutside, useMatchMedia } from 'stimulus-use';

import mediaQueries from '../../utils/media-queries';
import { transition } from '../../utils/transition';

export default class extends Controller {
    static targets = ['collapsable'];

    static outlets = ['header--navigation', 'slideover'];

    static values = {
        open: Boolean,
        menuLevel: Number,
    };

    #trap = null;

    connect() {
        this.isDesktopView = false;

        useClickOutside(this);
        useMatchMedia(this, { mediaQueries: mediaQueries['2xl'] });
    }

    async openValueChanged() {
        if (this.isDesktopView) {
            await this.#handleDesktopView();
        } else {
            await this.#handleMobileView();
        }

        await this.dispatch('toggle', { detail: { open: this.openValue, menuLevel: this.menuLevelValue } });

        if (this.openValue) {
            this.#trap?.activate();
        }
    }

    isDesktop() {
        this.isDesktopView = true;
    }

    notDesktop() {
        this.isDesktopView = false;

        this.hide();
        this.closeNestedMenus();
    }

    toggle() {
        this.openValue = !this.openValue;
    }

    show() {
        this.openValue = true;
    }

    hide() {
        this.openValue = false;
    }

    clickOutside() {
        if (this.isDesktopView && this.menuLevelValue === 1) {
            this.hide();
            this.closeNestedMenus();
        }
    }

    closeNestedMenus(menuLevel = null) {
        if (menuLevel !== null && menuLevel <= this.menuLevelValue) {
            this.hide();
        }

        if (!menuLevel || menuLevel < this.menuLevelValue) {
            this.slideoverOutlets.forEach((element) => {
                element.close();
            });
        }

        this.headerNavigationOutlets.forEach((outlet) => {
            outlet.closeNestedMenus(menuLevel);
        });
    }

    async #handleMobileView() {
        if (this.hasSlideoverOutlet) {
            if (this.openValue) {
                await this.slideoverOutlet.open();

                this.#trap = createFocusTrap([
                    document.getElementById('header-breadcrumb'),
                    this.slideoverOutlet.drawerTarget,
                ], {
                    allowOutsideClick: true,
                    initialFocus: () => this.slideoverOutlet.drawerTarget.querySelector('[data-menu-level]') || undefined,
                });
            } else {
                this.#trap?.deactivate();
                this.#trap = null;

                this.slideoverOutlet.close();
            }
        }

        this.element.closest('[data-header--navigation-target="collapsable"]')?.classList.toggle('tw-overflow-hidden', this.openValue);
    }

    async #handleDesktopView() {
        await transition(this.collapsableTarget, this.openValue);

        if (this.openValue) {
            this.#openNestedMenus(this.menuLevelValue);

            if (this.menuLevelValue === 1) {
                this.#trap = createFocusTrap(this.collapsableTarget, {
                    allowOutsideClick: true,
                    initialFocus: () => this.collapsableTarget.querySelector('[data-menu-level="1"]') || undefined,
                });
            }
        } else if (this.menuLevelValue === 1) {
            this.#trap.deactivate();
            this.#trap = null;
        }

        if (this.collapsableTarget.id) {
            document.querySelectorAll(`[aria-controls="${this.collapsableTarget.id}"]`).forEach((element) => {
                element.ariaExpanded = this.openValue;
            });
        }

        this.element.closest('[data-header--navigation-target="collapsable"]')?.classList.toggle('tw-overflow-hidden', this.openValue);
    }

    #openNestedMenus(menuLevel = null) {
        if (menuLevel !== null && menuLevel <= this.menuLevelValue) {
            this.show();
        }

        if (!menuLevel || menuLevel < this.menuLevelValue) {
            this.slideoverOutlets.forEach((element) => {
                element.open();
            });
        }

        this.headerNavigationOutlets.forEach((outlet) => {
            outlet.#openNestedMenus(menuLevel);
        });
    }
}
